
import { useState, useEffect, useRef } from 'react';

export default function SkillsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const skillCategories = [
    {
      title: 'Backend',
      sections: [
        {
          subtitle: 'Languages & Core',
          skills: [
            { name: 'Java', icon: 'ri-code-s-slash-line' },
            { name: 'Go', icon: 'ri-code-s-slash-line' }
          ]
        },
        {
          subtitle: 'Spring Ecosystem',
          skills: [
            { name: 'Spring Boot', icon: 'ri-leaf-line' },
            { name: 'Spring MVC', icon: 'ri-leaf-line' },
            { name: 'Spring Data JPA', icon: 'ri-database-2-line' },
            { name: 'Querydsl', icon: 'ri-search-line' },
            { name: 'Spring Security', icon: 'ri-shield-check-line' },
            { name: 'Spring WebFlux', icon: 'ri-leaf-line' },
            { name: 'MyBatis', icon: 'ri-database-2-line' }
          ]
        },
        {
          subtitle: 'Testing',
          skills: [
            { name: 'JUnit', icon: 'ri-test-tube-line' },
            { name: 'Mockito', icon: 'ri-test-tube-line' }
          ]
        }
      ]
    },
    {
      title: 'Infrastructure / DevOps',
      sections: [
        {
          subtitle: 'Database / Storage',
          skills: [
            { name: 'MySQL', icon: 'ri-database-2-line' },
            { name: 'MariaDB', icon: 'ri-database-2-line' },
            { name: 'Redis', icon: 'ri-flashlight-line' },
            { name: 'MongoDB', icon: 'ri-database-2-line' }
          ]
        },
        {
          subtitle: 'Messaging & Streaming',
          skills: [
            { name: 'Kafka', icon: 'ri-message-3-line' },
            { name: 'RabbitMQ', icon: 'ri-message-3-line' }
          ]
        },
        {
          subtitle: 'CI/CD & Automation',
          skills: [
            { name: 'Jenkins', icon: 'ri-tools-line' },
            { name: 'GitHub Actions', icon: 'ri-github-line' },
            { name: 'AWS CodeDeploy', icon: 'ri-cloud-line' },
            { name: 'Docker', icon: 'ri-container-line' }
          ]
        },
        {
          subtitle: 'Server / Networking',
          skills: [
            { name: 'Nginx', icon: 'ri-server-line' },
            { name: 'Tomcat', icon: 'ri-server-line' }
          ]
        },
        {
          subtitle: 'Cloud (AWS)',
          skills: [
            { name: 'EC2', icon: 'ri-cloud-line' },
            { name: 'S3', icon: 'ri-folder-cloud-line' },
            { name: 'RDS', icon: 'ri-database-2-line' },
            { name: 'ElasticCache(REDIS)', icon: 'ri-flashlight-line' },
            { name: 'ELB', icon: 'ri-scales-line' },
            { name: 'Route53', icon: 'ri-route-line' },
            { name: 'CloudWatch', icon: 'ri-eye-line' }
          ]
        },
        {
          subtitle: 'Monitoring / Observability',
          skills: [
            { name: 'Prometheus', icon: 'ri-line-chart-line' },
            { name: 'Grafana', icon: 'ri-dashboard-line' },
            { name: 'Spring Actuator', icon: 'ri-pulse-line' }
          ]
        },
        {
          subtitle: 'Performance Testing',
          skills: [
            { name: 'JMeter', icon: 'ri-speed-line' },
            { name: 'nGrinder', icon: 'ri-speed-line' }
          ]
        }
      ]
    },
    {
      title: 'Frontend',
      sections: [
        {
          subtitle: '',
          skills: [
            { name: 'JavaScript(ES6)', icon: 'ri-javascript-line' },
            { name: 'React', icon: 'ri-reactjs-line' },
            { name: 'Angular', icon: 'ri-angular-js-line' },
            { name: 'Flutter', icon: 'ri-flutter-line' },
            { name: 'WebSocket', icon: 'ri-wifi-line' }
          ]
        }
      ]
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-[#0d1117]">
      <div className="max-w-6xl mx-auto">
        <div className={`transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            <span className="bg-gradient-to-r from-[#3ECF8E] to-[#2dd4bf] bg-clip-text text-transparent">
              Skills
            </span>
          </h2>

          <div className="space-y-12">
            {skillCategories.map((category, categoryIndex) => (
              <div
                key={category.title}
                className={`relative bg-gradient-to-br from-[#161b22] to-[#0d1117] border border-[#30363d] rounded-2xl p-8 md:p-10 hover:border-[#3ECF8E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#3ECF8E]/10 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
                style={{ transitionDelay: `${categoryIndex * 200}ms` }}
              >
                {/* Glow effect */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#3ECF8E]/5 rounded-full blur-3xl" />
                
                <div className="relative">
                  {/* Category Title */}
                  <h3 className="text-2xl md:text-3xl font-bold mb-8 text-[#3ECF8E]">
                    {category.title}
                  </h3>

                  {/* All sections in one card */}
                  <div className="space-y-8">
                    {category.sections.map((section, sectionIndex) => (
                      <div key={`${category.title}-${sectionIndex}`} className="space-y-4">
                        {/* Section Subtitle */}
                        {section.subtitle && (
                          <h4 className="text-base font-semibold text-gray-400 uppercase tracking-wider mb-3">
                            {section.subtitle}
                          </h4>
                        )}

                        {/* Skills Grid */}
                        <div className="flex flex-wrap gap-3">
                          {section.skills.map((skill, skillIndex) => (
                            <div
                              key={skill.name}
                              className={`inline-flex items-center gap-2 px-4 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg hover:border-[#3ECF8E]/50 hover:bg-[#161b22] transition-all duration-300 ${
                                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                              }`}
                              style={{ transitionDelay: `${(categoryIndex * 200) + (sectionIndex * 50) + (skillIndex * 20)}ms` }}
                            >
                              <i className={`${skill.icon} text-[#3ECF8E] text-base`} />
                              <span className="text-sm text-gray-300 hover:text-[#3ECF8E] transition-colors duration-300 font-medium">
                                {skill.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Tech Highlights */}
          {/* <div className="mt-16 grid md:grid-cols-2 gap-6">
            <div className="relative bg-gradient-to-br from-[#161b22] to-[#0d1117] border border-[#30363d] rounded-2xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#3ECF8E]/10">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#3ECF8E]/5 rounded-full blur-3xl" />
              <div className="relative">
                <h3 className="text-xl font-bold mb-4 text-[#3ECF8E]">
                  성능 최적화 전문
                </h3>
                <p className="text-gray-300 leading-relaxed text-sm">
                  대규모 트래픽 처리 경험을 바탕으로 <strong className="text-white">쿼리 최적화</strong>, 
                  <strong className="text-white"> 캐싱 전략</strong>, <strong className="text-white">동시성 제어</strong> 등 
                  시스템 성능 개선에 특화된 역량을 보유하고 있습니다.
                </p>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-[#161b22] to-[#0d1117] border border-[#30363d] rounded-2xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#3ECF8E]/10">
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#2dd4bf]/5 rounded-full blur-3xl" />
              <div className="relative">
                <h3 className="text-xl font-bold mb-4 text-[#3ECF8E]">
                  안정성 & 모니터링
                </h3>
                <p className="text-gray-300 leading-relaxed text-sm">
                  <strong className="text-white">실시간 모니터링</strong>, <strong className="text-white">장애 대응</strong>, 
                  <strong className="text-white"> CI/CD 파이프라인</strong> 구축을 통해 
                  안정적이고 지속 가능한 서비스 운영 경험을 쌓았습니다.
                </p>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </section>
  );
}